import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import React, { createContext, HTMLAttributes, useContext } from 'react'

export const CardContext = createContext<{
  depth: number
}>({
  depth: 0,
})

const cardVariants = cva('border rounded-md', {
  variants: {
    size: {
      default: '',
      sm: 'py-2 px-3',
      lg: 'p-6',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

type Props = HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>

const Card = React.forwardRef<HTMLDivElement, Props>(
  ({ children, className, size, ...props }, ref) => {
    const { depth } = useContext(CardContext)
    const background = useContrastedBackground()

    return (
      <CardContext.Provider value={{ depth: depth + 1 }}>
        <div
          ref={ref}
          className={cn(
            cardVariants({ size }),
            background,
            size === undefined || size === 'default'
              ? depth === 0
                ? 'p-4'
                : 'py-2 px-3'
              : '',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </CardContext.Provider>
    )
  },
)
Card.displayName = 'Card'

export const useContrastedBackground = (): string => {
  const { depth } = useContext(CardContext)
  return depth % 2 === 0
    ? 'bg-card hover:bg-card'
    : 'bg-card-nested hover:bg-card-nested'
}

export const useContrastedFill = (): string => {
  const { depth } = useContext(CardContext)
  return depth % 2 === 0 ? 'text-card' : 'text-card-nested'
}

export default Card
